import React, { useEffect, useState } from 'react';
import { Badge, Form, Button, Spinner } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

/**
 * "+ Add category" affordance on the Food page (#127 part b).
 *
 * A signed-in user proposes a real category (Bread / Pastry / …) for this dish —
 * either an existing one from the catalog or a brand-new name — which is
 * admin-moderated (the #94 pattern) before it appears. A pending suggestion does
 * NOT change the dish's categories, so the page shows a "pending review" note
 * rather than adding a badge. Guests get a sign-in nudge (auth-only, like #94).
 */
function AddDishCategory({ foodId, existingCategories = [] }) {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success'|'muted'|'danger', msg }

  // Load the catalog category list once, when the picker first opens.
  useEffect(() => {
    if (!open || allCategories.length) return;
    let cancelled = false;
    fetch(`${API_BASE_URL}/foods/categories/`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => { if (!cancelled) setAllCategories(Array.isArray(data) ? data : []); })
      .catch(() => { /* a failed list just means no typeahead — free-text still works */ });
    return () => { cancelled = true; };
  }, [open, allCategories.length]);

  const existingIds = new Set(existingCategories.map((c) => c.id));
  const existingNames = new Set(existingCategories.map((c) => (c.name || '').toLowerCase()));

  const submit = async (e) => {
    e.preventDefault();
    const name = value.trim();
    if (!name) return;

    // Already on this dish? Nothing to suggest.
    if (existingNames.has(name.toLowerCase())) {
      setFeedback({ type: 'muted', msg: 'This dish already has that category.' });
      return;
    }

    // Match an existing catalog category by name → suggest it by id; otherwise
    // propose a new name (the backend collapses a name that matches anyway).
    const match = allCategories.find((c) => (c.name || '').toLowerCase() === name.toLowerCase());
    const body = match
      ? { food_id: foodId, category_id: match.id }
      : { food_id: foodId, proposed_name: name };

    setBusy(true);
    setFeedback(null);
    try {
      const res = await fetch(`${API_BASE_URL}/category-suggestions/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.status === 201) {
        setFeedback({ type: 'success', msg: 'Thanks! Your category suggestion is pending review.' });
        setValue('');
        setOpen(false);
      } else if (res.status === 409) {
        const d = await res.json().catch(() => ({}));
        setFeedback({ type: 'muted', msg: d.detail || 'That category is already suggested or assigned.' });
      } else if (res.status === 401) {
        setFeedback({ type: 'muted', msg: 'Please sign in to suggest a category.' });
      } else {
        setFeedback({ type: 'danger', msg: 'Could not submit — please try again.' });
      }
    } catch {
      setFeedback({ type: 'danger', msg: 'Could not submit — please try again.' });
    } finally {
      setBusy(false);
    }
  };

  // Guests: a gentle nudge, no picker (auth-only, mirrors #94 contributions).
  if (!token) {
    return (
      <span className="text-muted" style={{ fontSize: '0.75rem' }}>
        <a href="/login">Sign in</a> to suggest a category
      </span>
    );
  }

  if (!open) {
    return (
      <span className="d-inline-flex align-items-center gap-2">
        <Badge
          bg="light"
          text="dark"
          pill
          className="border"
          role="button"
          style={{ cursor: 'pointer', fontSize: '0.75rem' }}
          onClick={() => { setOpen(true); setFeedback(null); }}
        >
          + Add category
        </Badge>
        {feedback && <FeedbackText feedback={feedback} />}
      </span>
    );
  }

  const term = value.trim().toLowerCase();
  const suggestions = allCategories
    .filter((c) => !existingIds.has(c.id) && (c.name || '').toLowerCase().includes(term))
    .slice(0, 6);

  return (
    <Form onSubmit={submit} className="d-inline-flex align-items-center gap-1 flex-wrap">
      <Form.Control
        list="dish-category-options"
        size="sm"
        autoFocus
        placeholder="Category (e.g. Pastry)"
        value={value}
        maxLength={80}
        disabled={busy}
        onChange={(e) => setValue(e.target.value)}
        style={{ width: 190 }}
      />
      <datalist id="dish-category-options">
        {suggestions.map((c) => <option key={c.id} value={c.name} />)}
      </datalist>
      <Button type="submit" size="sm" variant="primary" disabled={busy || !value.trim()}>
        {busy ? <Spinner animation="border" size="sm" /> : 'Suggest'}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="link"
        className="text-muted p-0 ms-1"
        style={{ textDecoration: 'none', fontSize: '0.8rem' }}
        onClick={() => { setOpen(false); setValue(''); }}
      >
        cancel
      </Button>
      {feedback && <FeedbackText feedback={feedback} />}
    </Form>
  );
}

function FeedbackText({ feedback }) {
  const colorClass =
    feedback.type === 'success' ? 'text-success'
      : feedback.type === 'danger' ? 'text-danger'
        : 'text-muted';
  return (
    <span className={`${colorClass}`} style={{ fontSize: '0.75rem' }}>
      {feedback.msg}
    </span>
  );
}

export default AddDishCategory;
